/**
 * The plant's wall clock, and the instant the API stores — LV §116, §197, #483.
 *
 * The API speaks UTC: `2026-10-16T06:00:00Z` (LV §116). A planner types the time the crew
 * is due at the plant, on the plant's clock (LV §197), and `<input type="datetime-local">`
 * hands that over as `2026-10-16T08:00`, with no zone at all. These two functions convert
 * between the two in the zone the server names for the plant (`Site.timezone`). They are
 * the inverse of `formatDateTime` and, like it, presentation rather than a rule: which zone
 * a plant is in is the server's to say (LV §134), and this file only does the arithmetic.
 *
 * No library. `Intl.DateTimeFormat` already carries every zone's rules, so the offset in
 * force at an instant is read from it and the rest is subtraction.
 *
 * ## The two hours a year that do not map one to one
 *
 * Shown for Europe/Berlin, where every DAH plant is today; the same holds in any zone.
 *
 * - **Spring forward** — last Sunday of March, 02:00 CET becomes 03:00 CEST. A local time
 *   in the gap (`02:30`) never happens at the plant. It is read with the offset in force
 *   *before* the change (`+01:00`), so it lands an hour later on the plant's clock:
 *   `02:30` is stored as `01:30Z`, which the plant's clock shows as `03:30`. That is what
 *   a browser does with such a time and what Temporal calls `compatible`.
 * - **Fall back** — last Sunday of October, 03:00 CEST becomes 02:00 CET, so `02:30`
 *   happens twice. The *first* of the two is taken, still in summer time (`+02:00`):
 *   `02:30` is stored as `00:30Z`. The second one (`01:30Z`) cannot be typed through this
 *   input; read back, it shows as `02:30` like the first. A form that sends only what was
 *   changed (as the planning dialog does) therefore never rewrites such a value merely by
 *   being opened and saved.
 *
 * When the plant's zone is not known yet — the site is still loading — both functions
 * fall back to the viewer's zone, exactly as `formatDateTime` does, so what the reader sees
 * and what they type agree with each other in the meantime.
 */

const MINUTE = 60_000;
const DAY = 86_400_000;

/**
 * The viewer's own zone, used when no plant zone is supplied.
 *
 * Was the literal `"Europe/Berlin"` in `formatDateTime`, which is the LV §197 defect: every
 * DAH site is in that zone today, so it looked correct and would have stayed looking
 * correct while being wrong for the first plant outside it — timestamps do not announce
 * that they were rendered in the wrong zone.
 *
 * The viewer's zone rather than a configured group default, because the cases divide
 * cleanly: a screen *about one plant* passes that plant's zone and this value is not
 * consulted; a cross-site list has no single correct zone, and the least surprising
 * answer there is the clock the reader is looking at. `resolvedOptions()` is wrapped
 * because a locked-down browser can leave it undefined, and a thrown formatter would
 * take out the whole table.
 */
export function viewerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/** One formatter per zone: building one is the expensive part of the whole conversion. */
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string | undefined): Intl.DateTimeFormat {
  const key = timeZone ?? "";
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      // `h23`, so midnight is `00` — `hour12: false` alone gives `24` in some engines.
      hourCycle: "h23",
      // Read, never shown: without it the year `0000` comes back as `1`, the era being
      // the only part that says it is 1 BC (see `wallClock`).
      era: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(key, formatter);
  }
  return formatter;
}

/**
 * The UTC date with exactly these fields — the month counted from 1, as written.
 *
 * Field by field, never `Date.UTC`: that reads a year from 0 to 99 as 1900-1999, so
 * `0026-10-16T08:00` — which the input accepts, and which `parseOrtszeit` reads as the
 * year 26 — would come back from the plant's clock nineteen centuries later and every
 * offset computed against it would be off by as much. The same holds for `new Date(y, …)`.
 */
function utcFields(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): Date {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hour, minute, second, 0);
  return date;
}

/**
 * The plant's clock at `instant`, as the epoch milliseconds of a UTC date carrying those
 * same fields. Seconds precision: nothing here is finer than that.
 */
function wallClock(instant: number, timeZone: string | undefined): number {
  const fields = new Map<string, number>();
  let beforeChrist = false;
  for (const part of formatterFor(timeZone).formatToParts(new Date(instant))) {
    if (part.type === "era") beforeChrist = part.value === "BC";
    else if (part.type !== "literal") fields.set(part.type, Number(part.value));
  }
  const field = (name: string): number => fields.get(name) ?? 0;
  // `Intl` counts 1 BC, 2 BC, …; `setUTCFullYear` and the input's `0000` count 0, -1, ….
  const year = beforeChrist ? 1 - field("year") : field("year");
  return utcFields(
    year,
    field("month"),
    field("day"),
    field("hour") % 24,
    field("minute"),
    field("second"),
  ).getTime();
}

/** The zone's offset from UTC at `instant`, in minutes, east positive. */
function offsetMinutes(instant: number, timeZone: string | undefined): number {
  return Math.round((wallClock(instant, timeZone) - instant) / MINUTE);
}

/** `2026-10-16T08:00`, optionally with seconds — what a `datetime-local` input yields. */
const ORTSZEIT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * The wall-clock fields of `text` as epoch milliseconds of a UTC date, or `null` when the
 * text is not a local date and time, or names one that no calendar has (`2026-02-30`).
 */
function parseOrtszeit(text: string): number | null {
  const match = ORTSZEIT.exec(text);
  if (!match) return null;
  // The seconds group is optional, so it may be missing from the match at run time.
  const [year = 0, month = 1, day = 1, hour = 0, minute = 0, second = 0] = match
    .slice(1)
    .map((part: string | undefined) => (part === undefined ? 0 : Number(part)));
  // `utcFields` rolls an impossible date over (`02-30` becomes `03-02`); comparing the
  // fields back is what tells the two apart.
  const date = utcFields(year, month, day, hour, minute, second);
  const consistent =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day &&
    date.getUTCHours() === hour &&
    date.getUTCMinutes() === minute &&
    date.getUTCSeconds() === second;
  return consistent ? date.getTime() : null;
}

/** Whether `text` is a local date and time `plantLocalToUtcIso` can convert. */
export function istOrtszeit(text: string): boolean {
  return parseOrtszeit(text.trim()) !== null;
}

/** The API's spelling of an instant: ISO 8601, UTC, `Z`, no milliseconds (LV §116). */
function apiInstant(instant: number): string {
  return new Date(instant).toISOString().replace(/\.000Z$/, "Z");
}

/**
 * A `datetime-local` value, read on the plant's clock, as the API's UTC instant.
 *
 * `2026-10-16T08:00` in `Europe/Berlin` is `2026-10-16T06:00:00Z`; in January it is
 * `…T07:00:00Z`. Empty input is `null` — the request's "no value". A time in the
 * spring-forward gap or the repeated autumn hour is resolved as the comment at the top of
 * this file says. Anything that is not a local date and time throws: the form's schema
 * (`ortszeit()`) refuses it first, so reaching here with one is a programming error, and
 * silently sending `null` would clear a stored value.
 */
export function plantLocalToUtcIso(
  local: string,
  timeZone?: string | null,
): string | null {
  const text = local.trim();
  if (text === "") return null;
  const wall = parseOrtszeit(text);
  if (wall === null) {
    throw new RangeError(`Keine Ortszeit im Format JJJJ-MM-TTThh:mm: "${text}"`);
  }
  const zone = timeZone ?? viewerTimeZone();
  // The offsets a day either side. In an ordinary hour they agree; around a change they
  // are the two the change switches between, which is every offset the answer can have.
  const before = offsetMinutes(wall - DAY, zone);
  const after = offsetMinutes(wall + DAY, zone);
  const matches = [before, after]
    .map((offset) => wall - offset * MINUTE)
    .filter((instant) => instant + offsetMinutes(instant, zone) * MINUTE === wall)
    .sort((a, b) => a - b);
  // None matches in the spring gap: the offset from before the change, as documented.
  return apiInstant(matches[0] ?? wall - before * MINUTE);
}

/**
 * An instant from the API, as the plant's clock shows it, in the form a `datetime-local`
 * input takes (`2026-10-16T08:00`). Empty, absent or unreadable input gives `""` — an
 * empty field rather than an invented time.
 */
export function utcIsoToPlantLocal(
  iso: string | null | undefined,
  timeZone?: string | null,
): string {
  if (!iso) return "";
  const instant = Date.parse(iso);
  if (Number.isNaN(instant)) return "";
  const wall = wallClock(instant, timeZone ?? viewerTimeZone());
  return new Date(wall).toISOString().slice(0, 16);
}
